/**
 * Page: Verification Login (Journey-Based)
 *
 * Page de vérification OTP pour les logins :
 * - Email OTP (état: otp_verification du journey LOGIN_EMAIL_OTP)
 * - SMS OTP (état: otp_verification du journey LOGIN_SMS_OTP)
 *
 * Cette page vérifie qu'un journey valide est en cours avant d'afficher le formulaire.
 */

import { authContext } from '@/scripts/utils/auth-context.js';
import { alert } from '@/scripts/utils/alert-message.js';
import { KratosFlows } from '@/scripts/api/ory-client.js';
import { initOTPInput } from '@/_includes/components/otp-input.js';

document.addEventListener('DOMContentLoaded', async () => {
  // ============================================
  // INITIALIZATION & VALIDATION
  // ============================================
  await authContext.init();

  // Initialiser le composant OTP
  initOTPInput();

  const currentJourney = authContext.getCurrentJourney();
  const currentState = authContext.getCurrentState();

  // ============================================
  // VALIDATION (Journey Step Pattern)
  // ============================================
  // Vérifier UNIQUEMENT journey.name + state.name (PAS les données)
  const isExpectedJourneyAndState =
    (currentJourney?.name === 'LOGIN_EMAIL_OTP' && currentState?.name === 'otp_verification') ||
    (currentJourney?.name === 'LOGIN_SMS_OTP' && currentState?.name === 'otp_verification') ||
    (currentJourney?.name === 'LOGIN_OIDC' && currentState?.name === 'account_linking');

  if (!isExpectedJourneyAndState) {
    // ❌ Pas le bon journey/state → CLEAR + REDIRIGER
    authContext.clearJourney();

    alert.error('#alert-container', 'Session expirée. Redirection...', {
      icon: true,
      closable: false,
      duration: 1500
    });

    setTimeout(() => {
      window.location.href = '/login.html';
    }, 1500);

    return; // Bloquer la page
  }

  // ✅ Bon journey + bon state → Afficher l'UI et setup listeners
  // La validation des données (identifier, flowId) est faite par journey.isValid()

  console.log('[Verification] Valid journey detected:', {
    journey: currentJourney.name,
    state: currentState.name,
    method: authContext.get('method')
  });

  // ============================================
  // UI SETUP
  // ============================================
  const identifier = authContext.get('identifier');
  const method = authContext.get('method');
  const flowId = authContext.get('flow');

  // Afficher l'identifier (email ou téléphone)
  const identifierDisplay = document.querySelector('.identifier-display');
  if (identifierDisplay) {
    identifierDisplay.textContent = identifier;
  }

  // Mettre à jour le texte selon la méthode
  const methodText = method === 'email' ? 'e-mail' : 'SMS';
  const instructionText = document.querySelector('.instruction-text');
  if (instructionText) {
    instructionText.textContent = `Nous avons envoyé un code de vérification à 6 chiffres par ${methodText}.`;
  }

  // ============================================
  // TIMER (60 secondes avant affichage du bouton "Renvoyer")
  // ============================================
  const timerElement = document.querySelector('[data-timer]');
  const timerSeconds = document.querySelector('[data-timer-seconds]');
  const resendBtn = document.querySelector('[data-resend-code]');

  let countdown = 60;
  const timerInterval = setInterval(() => {
    countdown--;
    if (timerSeconds) timerSeconds.textContent = countdown;

    if (countdown <= 0) {
      clearInterval(timerInterval);
      if (timerElement) timerElement.classList.add('hidden');
      if (resendBtn) resendBtn.classList.remove('hidden');
    }
  }, 1000);

  // ============================================
  // FORM SUBMISSION
  // ============================================
  const form = document.querySelector('form');
  const submitButton = form?.querySelector('button[type="submit"]');
  const codeInput = form?.querySelector('[data-otp-value]'); // Input hidden contenant le code

  if (form && submitButton && codeInput) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      submitButton.disabled = true;
      submitButton.textContent = 'Vérification...';

      try {
        const code = codeInput.value;

        if (!code || code.length !== 6) {
          alert.error('#alert-container', 'Veuillez saisir un code à 6 chiffres.', { icon: true, closable: true });
          submitButton.disabled = false;
          submitButton.textContent = 'Vérifier';
          return;
        }

        // Vérifier si on est en mode account linking
        const isAccountLinking = authContext.get('accountLinkingRequired');
        const linkingFlowId = authContext.get('linkingFlowId');

        if (isAccountLinking && linkingFlowId) {
          // Mode account linking : soumettre directement au flow OIDC
          console.log('[Verification] Account linking mode, verifying code with flow:', linkingFlowId);

          const flow = await KratosFlows.getFlow(linkingFlowId, 'login');
          const csrfToken = flow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

          // Soumettre le code OTP pour lier les comptes
          const result = await KratosFlows.submitFlow(linkingFlowId, 'login', {
            method: 'code',
            code: code,
            identifier: identifier,
            csrf_token: csrfToken,
          });

          // Vérifier si session créée
          if (result.session) {
            authContext.save('session', result.session);
            authContext.clearJourney();
            alert.success('#alert-container', 'Compte Google lié avec succès !', {
              icon: true,
              closable: false,
              duration: 2000
            });
            setTimeout(() => {
              window.location.href = '/settings.html';
            }, 2000);
          }
        } else {
          // Mode login normal : utiliser le journey
          authContext.save('code', code);
          await authContext.executeCurrentState();
        }

      } catch (error) {
        console.error('[Verification] Error verifying code:', error);

        // Réactiver le bouton
        submitButton.disabled = false;
        submitButton.textContent = 'Vérifier';

        // L'erreur a déjà été affichée par l'action verify_otp
        // On ne fait rien ici pour éviter de dupliquer les messages
      }
    });
  }

  // ============================================
  // RESEND CODE
  // ============================================
  if (resendBtn) {
    resendBtn.addEventListener('click', async () => {
      resendBtn.disabled = true;
      const originalText = resendBtn.textContent;
      resendBtn.textContent = 'Envoi en cours...';

      try {
        // Créer un nouveau flow
        const flow = await KratosFlows.initLoginFlow();
        const csrfToken = flow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

        // Soumettre à nouveau avec identifier
        try {
          await KratosFlows.submitFlow(flow.id, 'login', {
            method: 'code',
            identifier: identifier,
            csrf_token: csrfToken,
          });
        } catch (submitError) {
          // 400 après envoi = normal
          const responseData = submitError.response?.data;
          const isCodeSent = submitError.response?.status === 400 &&
            (responseData?.ui?.messages?.some(m => m.type === 'info') || responseData?.state === 'sent_email');

          if (!isCodeSent) {
            throw submitError;
          }
        }

        // Mettre à jour le flowId pour le prochain code OTP
        authContext.save('flow', flow.id);

        alert.success('#alert-container', 'Un nouveau code a été envoyé.', { icon: true, closable: true, duration: 5000 });

        // Réinitialiser le timer
        resendBtn.classList.add('hidden');
        if (timerElement) timerElement.classList.remove('hidden');
        countdown = 60;
        if (timerSeconds) timerSeconds.textContent = countdown;

        const newTimerInterval = setInterval(() => {
          countdown--;
          if (timerSeconds) timerSeconds.textContent = countdown;

          if (countdown <= 0) {
            clearInterval(newTimerInterval);
            if (timerElement) timerElement.classList.add('hidden');
            if (resendBtn) resendBtn.classList.remove('hidden');
          }
        }, 1000);

      } catch (error) {
        console.error('[Verification] Error resending code:', error);

        const errorMsg = error.response?.data?.ui?.messages?.find(m => m.type === 'error')?.text
          || 'Erreur lors de l\'envoi du code. Veuillez réessayer.';

        alert.error('#alert-container', errorMsg, { icon: true, closable: true });

        resendBtn.disabled = false;
        resendBtn.textContent = originalText;
      }
    });
  }

  // ============================================
  // BACK BUTTON
  // ============================================
  const backButton = document.querySelector('[data-back-to-login]');
  if (backButton) {
    backButton.addEventListener('click', () => {
      // Nettoyer le journey et retourner au login
      authContext.clearJourney();
      window.location.href = '/login.html';
    });
  }
});
